// File: webelieve-frontend/src/features/intake/components/LeadCard.tsx
// EF-2.2: Lead card for pipeline Kanban view

import React from 'react';
import type { LeadSummary } from '../api/intakeCrmApi';

interface LeadCardProps {
  lead: LeadSummary;
  onClick: () => void;
}

const LeadCard: React.FC<LeadCardProps> = ({ lead, onClick }) => {
  const timeInStage = getTimeInStage(lead.createdAt);

  return (
    <div
      onClick={onClick}
      className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3 cursor-pointer hover:shadow-md hover:border-lex-accent transition-all"
    >
      {/* Name + Practice Area */}
      <div className="flex items-start justify-between mb-2">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white truncate flex-1">
          {lead.clientName}
        </h4>
        {lead.practiceArea && (
          <span className="ml-2 px-1.5 py-0.5 text-[10px] font-medium rounded bg-lex-accent-soft text-lex-accent whitespace-nowrap">
            {lead.practiceArea}
          </span>
        )}
      </div>

      {/* Score + Classification */}
      {lead.leadScore != null && (
        <div className="flex items-center gap-2 mb-2">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white ${getScoreColor(lead.leadScore)}`}>
            {Math.round(lead.leadScore)}
          </div>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {lead.leadScore >= 70 ? 'Strong' : lead.leadScore >= 40 ? 'Moderate' : 'Weak'} lead
          </span>
        </div>
      )}

      {/* Contact info */}
      <div className="space-y-0.5 mb-2">
        {lead.email && (
          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
            {lead.email}
          </p>
        )}
        {lead.phone && (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {lead.phone}
          </p>
        )}
      </div>

      {/* Footer: time in stage + doc count */}
      <div className="flex items-center justify-between text-[10px] text-gray-400 dark:text-gray-500 pt-1 border-t border-gray-100 dark:border-gray-700">
        <span>{timeInStage}</span>
        <div className="flex items-center gap-2">
          {lead.documentCount > 0 && (
            <span className="flex items-center gap-0.5">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              {lead.documentCount}
            </span>
          )}
          {lead.contactAttempts > 0 && (
            <span className="flex items-center gap-0.5">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              {lead.contactAttempts}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

function getScoreColor(score: number): string {
  if (score >= 70) return 'bg-lex-success';
  if (score >= 40) return 'bg-lex-warning';
  return 'bg-lex-danger';
}

function getTimeInStage(createdAt: string): string {
  const diff = Date.now() - new Date(createdAt).getTime();
  const hours = Math.floor(diff / (1000 * 60 * 60));
  if (hours < 1) return 'Just now';
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return '1d ago';
  return `${days}d ago`;
}

export default LeadCard;
